/**
 * Signup preset codes: the `/signup?code=<code>` special-link mechanism.
 *
 * A code is a staff-minted, opaque token that maps to an
 * `org_provisioning_preset` row OTHER than the default `trial-default` one.
 * When a brand-new organization is created for a signup that carried a valid
 * code, that named preset is applied instead of the default — through the very
 * same `applyToNewOrganization` path, so the resulting org is indistinguishable
 * from one a CSM configured by hand.
 *
 * The code is deliberately NOT the preset key itself. Preset keys are readable
 * policy names, and a preset hands out `use:*` permission grants — putting the
 * key in the URL would let anyone who guesses or shares the string self-serve
 * elevated features indefinitely. A code row adds what a bare key cannot:
 * revocation (disable the row), expiry, an optional redemption cap, and an
 * audit trail of which orgs came from which link.
 *
 * A signup with no code, an invalid code, or a revoked/expired/exhausted code
 * behaves exactly like today: the org gets `trial-default`. The code path can
 * only ever SUBSTITUTE which preset is applied; it can never fail a signup.
 */

/** Query parameter read on `/signup` (and `/login`): `/signup?code=<code>`. */
export const SIGNUP_PRESET_PARAM = "code";

/**
 * The path a MINTED, shareable signup link must use.
 *
 * BRE-6058: the app sets `trailingSlash: true` (packages/frontend/next.config.js),
 * so `/signup` 308-redirects to `/signup/` before any of our code runs. Reading
 * a code still works either way — the middleware matches on the slash-stripped
 * pathname — but anywhere we MINT a link for a human to click (the internal
 * panel's copy button, campaigns, docs) should emit this form so the click
 * lands directly instead of bouncing.
 *
 * It lives beside `SIGNUP_PRESET_PARAM` because the two are only ever used
 * together, and so that a future change to the trailing-slash behaviour (e.g.
 * adopting `skipTrailingSlashRedirect`) has one place to look rather than a
 * hunt through every call site that builds a link.
 */
export const SIGNUP_PRESET_LINK_PATH = "/signup/";

/**
 * Cookie that carries the code from the signup page to org creation.
 *
 * Set by the frontend middleware when the param is present, read back in
 * `triggerOnboarding` (the chokepoint every auth-completion path funnels
 * through) — the same mechanism GCLID attribution uses to survive the WorkOS
 * OAuth round-trip and the password/email-verification flows alike.
 */
export const SIGNUP_PRESET_COOKIE = "brev_signup_preset";

/**
 * Cookie lifetime. Long enough to cover an OAuth round-trip or an email
 * verification detour. The cookie is deleted as soon as an onboarding
 * callback consumes it, so this age only governs ABANDONED flows — a visitor
 * who clicked the link but never finished signing up. The authoritative
 * checks (disabled, expiry, redemption cap) happen server-side at redemption
 * regardless.
 */
export const SIGNUP_PRESET_COOKIE_MAX_AGE_SECONDS = 2 * 60 * 60;

/** `org_signup_preset_code.code` is varchar(64). */
export const MAX_SIGNUP_PRESET_CODE_LENGTH = 64;

/**
 * Floor on code length. Deliberately short (BRE-6058) so staff can mint terse
 * vanity codes for print, conference badges, and spoken URLs — `?code=pi7` is
 * legible read aloud in a way a 24-char token is not.
 *
 * What this floor does NOT do is make a code unguessable; only length and
 * randomness do that, and a vanity code has given up both by definition. At
 * three characters the space of MEMORABLE strings is small enough that a
 * curious visitor typing `?code=vip` may simply land on a real cohort's preset.
 * That is why `generateCode()` remains the default (24 random chars) and the
 * mint form warns on any custom code: a short code should be paired with an
 * expiry and a redemption cap, which are what actually bound the damage.
 *
 * The floor is not a security control either way — it is a hygiene gate that
 * keeps junk out of cookies and log lines. Whether a code is real, enabled,
 * unexpired and under its cap is decided server-side at redemption, and
 * redeeming costs a full signup (work-email account creation, Radar-screened)
 * per attempt, which is what makes enumeration impractical rather than the
 * length.
 */
export const MIN_SIGNUP_PRESET_CODE_LENGTH = 3;

/**
 * Cheap shape gate applied wherever a code crosses a trust boundary (URL
 * param, cookie) BEFORE it is looked up. Not a security check — the lookup
 * is — but it keeps arbitrary attacker-shaped strings out of cookies and
 * log lines. Matches base64url, which is also what minted codes are made of;
 * staff-chosen vanity codes must fit the same alphabet.
 */
export function isPlausibleSignupPresetCode(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= MIN_SIGNUP_PRESET_CODE_LENGTH &&
    value.length <= MAX_SIGNUP_PRESET_CODE_LENGTH &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

/** One code row as the internal panel sees it. */
export interface OrgSignupPresetCodeView {
  code: string;
  presetKey: string;
  /** Operator-facing note ("Design partners H2'26"); never shown to customers. */
  label: string;
  disabled: boolean;
  expiresAt: Date | null;
  /** `null` = unlimited. */
  maxRedemptions: number | null;
  redeemedCount: number;
  createdAt: Date;
  createdByUuid: string | null;
}

/**
 * A successful atomic claim of one redemption slot, made just before the org
 * is created. `codeId` lets the post-creation audit row reference the code
 * without a second lookup racing a concurrent disable.
 */
export interface ClaimedSignupPresetCode {
  codeId: number;
  presetKey: string;
}
